import React, { useState, useEffect } from 'react';
import Button from './ui/Button';
import Input from './ui/Input';

const EditProfileModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  user,
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user && isOpen) {
      setFormData({
        name: user.name || '',
        phone: user.phone || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    }
  }, [user, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Nome deve ter pelo menos 2 caracteres';
    } else if (formData.name.length > 50) {
      newErrors.name = 'Nome deve ter no máximo 50 caracteres';
    }

    if (formData.phone && !/^[\d\s\-+()]+$/.test(formData.phone)) {
      newErrors.phone = 'Telefone deve conter apenas números, espaços, hífens, parênteses ou sinal de +';
    }

    // Validação de senha (opcional - só valida se preenchida)
    if (formData.newPassword || formData.currentPassword || formData.confirmPassword) {
      if (!formData.currentPassword) {
        newErrors.currentPassword = 'Senha atual é obrigatória para alteração';
      }
      
      if (!formData.newPassword) {
        newErrors.newPassword = 'Nova senha é obrigatória';
      } else if (formData.newPassword.length < 6) {
        newErrors.newPassword = 'Nova senha deve ter pelo menos 6 caracteres';
      }
      
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Confirmação de senha é obrigatória';
      } else if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Senhas não coincidem';
      }
      
      if (formData.currentPassword === formData.newPassword) {
        newErrors.newPassword = 'A nova senha deve ser diferente da atual';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  };

  const handleClose = () => {
    setFormData({ 
      name: '', 
      phone: '', 
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black opacity-25" onClick={handleClose}></div>
        
        <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-white/10 shadow-2xl shadow-xl max-w-md w-full mx-4 p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-white">
              Editar Perfil
            </h3>
            <p className="text-sm text-gray-400">
              Atualize suas informações pessoais
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                label="Nome *"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={errors.name}
                disabled={loading}
                placeholder="Digite seu nome completo"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Email
              </label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
                placeholder="Email não pode ser alterado"
              />
              <p className="text-xs text-gray-500 mt-1">O email não pode ser alterado por questões de segurança</p>
            </div>

            <div>
              <Input
                label="Telefone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                error={errors.phone}
                disabled={loading}
                placeholder="Digite seu telefone (opcional)"
              />
            </div>

            <div className="border-t pt-4 mt-6">
              <h4 className="text-md font-medium text-white mb-3">Alterar Senha</h4>
              <p className="text-sm text-gray-600 mb-4">Deixe em branco para manter a senha atual</p>
              
              <div className="space-y-3">
                <Input
                  label="Senha Atual"
                  type="password"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  error={errors.currentPassword}
                  disabled={loading}
                  placeholder="Digite sua senha atual"
                />

                <Input
                  label="Nova Senha"
                  type="password"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  error={errors.newPassword}
                  disabled={loading}
                  placeholder="Digite a nova senha (min. 6 caracteres)"
                />

                <Input
                  label="Confirmar Nova Senha"
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={errors.confirmPassword}
                  disabled={loading}
                  placeholder="Confirme a nova senha"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                loading={loading}
                disabled={loading}
              >
                Salvar Alterações
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProfileModal;